import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { HTMLTemplateModel } from '../../models/html.template.model';
import { DocumentTemplateModel } from '../../models/document.template.model';
import { revertAll, RootState } from '../../store';

/** Route param value that marks a user's own (company) template instead of a library item. */
export const COMPANY_TEMPLATES_CATEGORY = 'company-templates';

export type LibraryItemDetailsModel = HTMLTemplateModel | DocumentTemplateModel;

const isCompanyTemplate = (templateCategory?: string): boolean =>
  templateCategory === COMPANY_TEMPLATES_CATEGORY;

const getLibraryItemEntity = (templateCategory?: string): string =>
  isCompanyTemplate(templateCategory) ? 'document-template' : 'document-library';

/* ------------------------------------------------------------------------------------------------
 * Fetching
 * --------------------------------------------------------------------------------------------- */

type LibraryItemDetailsRequest = {
  id: number;
  /** `name` route param: 'company-templates' or a library category. */
  templateCategory: string;
};

/**
 * Fetches the HTML of a library (non company) item.
 * Company templates carry their HTML in `content`, so they don't need this request.
 */
export const fetchLibraryItemHTML = createAsyncThunk(
  'libraryItemDetails/fetchHTML',
  async (id: number): Promise<string> => {
    const response = await NWClient.get<{ id: number; html: string }>('document-library-html', id);
    return response.html;
  }
);

/**
 * Fetches the library item / company template.
 * Company templates carry their HTML in `content`, which is stored right away. For library items
 * the HTML has to be requested afterwards with `fetchLibraryItemHTML`, so the item itself is still
 * available when only the HTML request fails.
 */
export const fetchLibraryItemDetails = createAsyncThunk(
  'libraryItemDetails/fetch',
  async (request: LibraryItemDetailsRequest): Promise<LibraryItemDetailsModel> => {
    const { id, templateCategory } = request;
    return NWClient.get<LibraryItemDetailsModel>(getLibraryItemEntity(templateCategory), id);
  }
);

/* ------------------------------------------------------------------------------------------------
 * Updating properties (name, category, tags) - company templates only
 * --------------------------------------------------------------------------------------------- */

type LibraryItemDetailsUpdatePayload = {
  user: number;
  name: string;
  category: string;
};

type LibraryItemDetailsTagsPayload = {
  user: number;
  dt_workflows: string[];
  dt_types: string[];
  dt_label: string[];
};

export type LibraryItemDetailsUpdateRequest = {
  id: number;
  userId: number;
  templateName: string;
  /** Used when `templateName` is empty, e.g. t('labels.templateDefault'). */
  defaultTemplateName: string;
  category: string;
  documentTypeLabel: string;
  documentWorkflowLabel: string;
  customLabel: string;
};

export const updateLibraryItemDetails = createAsyncThunk(
  'libraryItemDetails/update',
  async (request: LibraryItemDetailsUpdateRequest): Promise<LibraryItemDetailsModel> => {
    const {
      id,
      userId,
      templateName,
      defaultTemplateName,
      category,
      documentTypeLabel,
      documentWorkflowLabel,
      customLabel,
    } = request;

    const itemPayload: LibraryItemDetailsUpdatePayload = {
      user: userId,
      name: templateName ? templateName : defaultTemplateName,
      category,
    };

    const tagsPayload: LibraryItemDetailsTagsPayload = {
      user: userId,
      dt_workflows: documentWorkflowLabel ? [documentWorkflowLabel] : [],
      dt_types: documentTypeLabel ? [documentTypeLabel] : [],
      dt_label: customLabel ? [customLabel] : [],
    };

    const item = await NWClient.patch<LibraryItemDetailsUpdatePayload>(
      `document-template/${id}/`,
      null,
      itemPayload
    );
    const tags = await NWClient.patch<LibraryItemDetailsTagsPayload>(
      'tag-document-template',
      id,
      tagsPayload
    );

    try {
      return await NWClient.get<HTMLTemplateModel>('document-template', id);
    } catch {
      // The update itself succeeded - fall back to the patched values if the refresh fails.
      return { ...item, ...tags, id } as unknown as LibraryItemDetailsModel;
    }
  }
);

/* ------------------------------------------------------------------------------------------------
 * Saving the edited HTML
 * --------------------------------------------------------------------------------------------- */

type LibraryItemContentPayload = {
  user: number;
  name: string;
  content?: string;
  html?: string;
};

export type LibraryItemContentSaveRequest = {
  id: number;
  templateCategory: string;
  userId: number;
  name: string;
  html: string;
};

export type LibraryItemContentSaveResult = {
  html: string;
  field: 'content' | 'html' | null;
};

export const saveLibraryItemContent = createAsyncThunk(
  'libraryItemDetails/saveContent',
  async (
    request: LibraryItemContentSaveRequest,
    { getState }
  ): Promise<LibraryItemContentSaveResult> => {
    const { id, templateCategory, userId, name, html } = request;
    const item = (getState() as RootState).libraryItemDetails.data;

    const data: LibraryItemContentPayload = { user: userId, name };
    let field: LibraryItemContentSaveResult['field'] = null;

    if (isCompanyTemplate(templateCategory)) {
      if (item && 'content' in item) {
        data.content = html;
        field = 'content';
      }
    } else if (item && 'html' in item) {
      data.html = html;
      field = 'html';
    }

    await NWClient.patch<LibraryItemContentPayload>(
      `${getLibraryItemEntity(templateCategory)}/${id}/`,
      null,
      data
    );

    return { html, field };
  }
);

/* ------------------------------------------------------------------------------------------------
 * Slice
 * --------------------------------------------------------------------------------------------- */

interface LibraryItemDetailsState {
  data: LibraryItemDetailsModel | null;
  html: string;
  status: string;
  htmlStatus: string;
  updateStatus: string;
  saveStatus: string;
  error: string | null;
}

const initialState: LibraryItemDetailsState = {
  data: null,
  html: '',
  status: 'idle',
  htmlStatus: 'idle',
  updateStatus: 'idle',
  saveStatus: 'idle',
  error: '',
};

const libraryItemDetailsSlice = createSlice({
  name: 'libraryItemDetails',
  initialState,
  reducers: {
    setLibraryItemHTML: (state, action: PayloadAction<string>) => {
      state.html = action.payload;
    },
    resetLibraryItemDetails: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLibraryItemDetails.pending, (state) => {
        state.data = null;
        state.html = '';
        state.status = 'loading';
        state.htmlStatus = 'loading';
        state.error = '';
      })
      .addCase(fetchLibraryItemDetails.fulfilled, (state, action) => {
        state.data = action.payload;
        state.status = 'succeeded';
        if (isCompanyTemplate(action.meta.arg.templateCategory)) {
          state.html = action.payload.content ?? '';
          state.htmlStatus = 'succeeded';
        }
      })
      .addCase(fetchLibraryItemDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.htmlStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(fetchLibraryItemHTML.pending, (state) => {
        state.htmlStatus = 'loading';
      })
      .addCase(fetchLibraryItemHTML.fulfilled, (state, action) => {
        state.html = action.payload;
        state.htmlStatus = 'succeeded';
      })
      .addCase(fetchLibraryItemHTML.rejected, (state, action) => {
        state.htmlStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(updateLibraryItemDetails.pending, (state) => {
        state.updateStatus = 'updating';
        state.error = '';
      })
      .addCase(updateLibraryItemDetails.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.data = action.payload;
      })
      .addCase(updateLibraryItemDetails.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(saveLibraryItemContent.pending, (state) => {
        state.saveStatus = 'saving';
        state.error = '';
      })
      .addCase(saveLibraryItemContent.fulfilled, (state, action) => {
        const { html, field } = action.payload;
        state.saveStatus = 'succeeded';
        state.html = html;
        if (state.data && field) {
          (state.data as { content?: string; html?: string })[field] = html;
        }
      })
      .addCase(saveLibraryItemContent.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(revertAll, () => initialState);
  },
});

export const { setLibraryItemHTML, resetLibraryItemDetails } = libraryItemDetailsSlice.actions;
export default libraryItemDetailsSlice.reducer;
export const selectLibraryItemDetails = (state: RootState) => state.libraryItemDetails.data;
export const selectLibraryItemHTML = (state: RootState) => state.libraryItemDetails.html;
export const selectLibraryItemDetailsStatus = (state: RootState) => state.libraryItemDetails.status;
export const selectLibraryItemHTMLStatus = (state: RootState) =>
  state.libraryItemDetails.htmlStatus;
export const selectLibraryItemDetailsUpdateStatus = (state: RootState) =>
  state.libraryItemDetails.updateStatus;
export const selectLibraryItemContentSaveStatus = (state: RootState) =>
  state.libraryItemDetails.saveStatus;
export const selectLibraryItemDetailsError = (state: RootState) => state.libraryItemDetails.error;
